import { request, requestBlobWithHeaders } from "../client"
import { invalidateOrganizationCaches } from "../organizations"

export type DemoDataResetAudience = "admin" | "employee"

export type DemoDataResetProfileInput = {
  access_email: string
  password: string | null
  reuse_user_id: string | null
  contact_email: string
  contact_mobile: string
}

export type DemoDataResetRequest = {
  preset_key: "sales_v1"
  confirmation: string
  profiles: Record<DemoDataResetAudience, DemoDataResetProfileInput>
  base_date: string | null
  calendar_projection: boolean
  target_plan_family: "kehrbase" | "kehrchat" | "kehrtalk"
}

export type DemoDataResetResultProfile = {
  access_email: string
  password_reused: boolean
}

export type DemoDataResetResponse = {
  demo_reset_id: string
  preset_key: string
  base_date: string
  counts: Record<string, number>
  calendar_sync_status: string
  profiles: Record<DemoDataResetAudience, DemoDataResetResultProfile>
  current_user_session_revoked: boolean
  warnings: string[]
}

export type DemoDataResetFormDefaultProfile = {
  access_email: string
  contact_email: string
  contact_mobile: string
  password_reusable: boolean
  reuse_user_id: string | null
}

export type DemoDataResetFormDefaults = {
  profiles: Record<DemoDataResetAudience, DemoDataResetFormDefaultProfile>
}

export type DemoDataResetJobStatus = "queued" | "running" | "succeeded" | "failed"
export type DemoDataResetPhase =
  | "validating"
  | "configuring"
  | "cleaning"
  | "creating_users"
  | "creating_registry"
  | "creating_work_orders"
  | "creating_schedule"
  | "creating_communications"
  | "finalizing"

export type DemoDataResetJobError = {
  code: string
  message: string
}

export type DemoDataResetJobView = {
  job_id: string
  organization_id: string
  source: "manual" | "cli" | "weekly" | "admin_quick"
  status: DemoDataResetJobStatus
  phase: DemoDataResetPhase | null
  completed_steps: number
  total_steps: number
  progress_percent: number
  attempt: number
  poll_after_ms: number
  created_at: string
  updated_at: string
  started_at: string | null
  finished_at: string | null
  next_attempt_at: string | null
  result: DemoDataResetResponse | null
  error: DemoDataResetJobError | null
}

export type DemoDataResetJobStartResponse = DemoDataResetJobView & {
  enqueue_disposition: "created" | "idempotent_replay" | "active_job"
  status_token: string
}

export type DemoDataResetStreetListDownload = {
  blob: Blob
  contentDisposition: string | null
}

export const getDemoOrganizationResetFormDefaults = (
  organizationId: string,
  options: { signal?: AbortSignal } = {},
): Promise<DemoDataResetFormDefaults> =>
  request<DemoDataResetFormDefaults>(
    `/organizations/${organizationId}/demo-reset-jobs/form-defaults`,
    {
      method: "GET",
      signal: options.signal,
    },
  )

export const enqueueDemoOrganizationResetJob = (
  organizationId: string,
  payload: DemoDataResetRequest,
  options: { idempotencyKey: string; signal?: AbortSignal },
): Promise<DemoDataResetJobStartResponse> =>
  request<DemoDataResetJobStartResponse>(`/organizations/${organizationId}/demo-reset-jobs`, {
    method: "POST",
    headers: { "Idempotency-Key": options.idempotencyKey },
    body: JSON.stringify(payload),
    signal: options.signal,
  })

export const getDemoOrganizationResetJob = (
  organizationId: string,
  jobId: string,
  options: { statusToken?: string | null; signal?: AbortSignal },
): Promise<DemoDataResetJobView> => {
  const headers = new Headers()

  if (options.statusToken) {
    headers.set("X-Demo-Reset-Status-Token", options.statusToken)
  }

  return request<DemoDataResetJobView>(
    `/organizations/${organizationId}/demo-reset-jobs/${jobId}`,
    {
      method: "GET",
      headers,
      signal: options.signal,
    },
  )
}

export const downloadDemoOrganizationResetStreetList = async (
  organizationId: string,
  jobId: string,
  audience: DemoDataResetAudience,
  options: { statusToken?: string | null; signal?: AbortSignal },
): Promise<DemoDataResetStreetListDownload> => {
  const headers = new Headers()

  if (options.statusToken) {
    headers.set("X-Demo-Reset-Status-Token", options.statusToken)
  }

  const response = await requestBlobWithHeaders(
    `/organizations/${organizationId}/demo-reset-jobs/${jobId}/street-lists/${audience}/download`,
    {
      method: "GET",
      headers,
      signal: options.signal,
    },
  )

  return {
    blob: response.data,
    contentDisposition: response.headers.get("content-disposition"),
  }
}

export const completeDemoOrganizationReset = (
  organizationId: string,
  result: DemoDataResetResponse,
): DemoDataResetResponse => {
  invalidateOrganizationCaches(organizationId)

  return result
}
